/**
 * Phase 7 Stage 9 Part G — Glossary terms.
 *
 * Single source of truth for all term definitions used by:
 *   - The Glossary page (full searchable view)
 *   - HelpTooltip components scattered across the app (inline tooltips)
 *
 * Each term has:
 *   id          — kebab-case key, used as the URL anchor and tooltip ref
 *   term        — display name
 *   short       — 1-line summary (used in tooltips)
 *   long        — full definition (used in glossary page)
 *   category    — section grouping
 *   aliases     — alternative search terms
 */
export const GLOSSARY_CATEGORIES = [
  { key: "valuation", label: "Valuation Terms" },
  { key: "returns", label: "Return Terms" },
  { key: "signals", label: "Signal Terms" },
  { key: "portfolio", label: "Portfolio Terms" },
  { key: "cse", label: "CSE-Specific Terms" },
  { key: "market-intelligence", label: "Market Intelligence & Macro" },
  { key: "tax", label: "Tax & Charges" },
];

export const GLOSSARY = [
  // ── Valuation ───────────────────────────────────────────────────────
  {
    id: "pe-ratio",
    term: "PE Ratio (Price to Earnings)",
    short: "How many years of current earnings you're paying for. Lower PE generally means cheaper.",
    long: "How many years of current earnings you are paying for. PE 10 means you pay Rs.10 for every Rs.1 of annual profit. Lower PE generally means cheaper — but always compare within the same sector.",
    category: "valuation",
    aliases: ["pe", "price earnings", "p/e"],
  },
  {
    id: "pb-ratio",
    term: "PB Ratio (Price to Book)",
    short: "Price vs net asset value. PB <1 may indicate undervaluation, especially for banks.",
    long: "How much you pay compared to the company's net asset value. PB below 1.0 means buying the company for less than its book value — often a value signal for banks and asset-heavy businesses.",
    category: "valuation",
    aliases: ["pb", "price book", "p/b"],
  },
  {
    id: "graham-number",
    term: "Graham Number",
    short: "Benjamin Graham's fair value formula: √(22.5 × EPS × BVPS).",
    long: "A fair value formula by legendary investor Benjamin Graham: square root of (22.5 × earnings per share × book value per share). If the current price is below the Graham Number the stock may be undervalued.",
    category: "valuation",
    aliases: ["graham", "intrinsic value"],
  },
  {
    id: "ddm-fair-value",
    term: "DDM Fair Value (Dividend Discount Model)",
    short: "Annual Dividend ÷ (Required Return − Dividend Growth). Useful for dividend-paying stocks.",
    long: "Estimates fair value based on dividend payments: Annual Dividend ÷ (Required Return − Dividend Growth). We use 12% required return and 5% growth. Useful for dividend-paying stocks. Does not apply to companies that pay no dividend.",
    category: "valuation",
    aliases: ["ddm", "dividend discount"],
  },
  {
    id: "sector-median-pe",
    term: "Sector Median PE",
    short: "Middle PE among all companies in the same sector — used as a benchmark.",
    long: "The middle PE ratio among all companies in the same sector. We compare each stock to this median to identify relatively cheap or expensive stocks within their peer group.",
    category: "valuation",
  },

  // ── Returns ─────────────────────────────────────────────────────────
  {
    id: "yield-on-cost",
    term: "Yield on Cost",
    short: "Annual dividend as % of YOUR purchase price (not today's market price).",
    long: "Your annual dividend as a percentage of what YOU paid — not today's price. If you bought at Rs.100 and receive Rs.7 dividend, your yield on cost is 7.00% even if the stock now trades at Rs.150 (where yield would be only 4.67%).",
    category: "returns",
    aliases: ["yoc"],
  },
  {
    id: "dividend-yield",
    term: "Dividend Yield",
    short: "Annual dividend as % of CURRENT share price. Changes daily.",
    long: "Annual dividend as a percentage of the CURRENT share price. Changes every day as the share price moves.",
    category: "returns",
  },
  {
    id: "unrealised-gain-loss",
    term: "Unrealised Gain/Loss",
    short: "Paper profit/loss on shares you still hold. Becomes real only when you sell.",
    long: "Paper profit or loss on shares you still hold. Becomes real (realised) only when you sell.",
    category: "returns",
  },
  {
    id: "annualised-return",
    term: "Annualised Return",
    short: "Total return converted to a yearly rate. e.g. 50% over 3 years ≈ 14.5% annualised.",
    long: "Total return converted to a yearly rate for comparison. A 50% return over 3 years equals approximately 14.5% per year annualised.",
    category: "returns",
  },
  {
    id: "capital-appreciation",
    term: "Capital Appreciation",
    short: "Increase in share price over time. Buy Rs.100, sell Rs.130 → +Rs.30 (30%).",
    long: "Increase in share price over time. If you buy at Rs.100 and sell at Rs.130, capital appreciation is Rs.30 (30%).",
    category: "returns",
  },
  {
    id: "total-return",
    term: "Total Return",
    short: "Dividends received + capital appreciation. The complete performance picture.",
    long: "Dividends received PLUS capital appreciation. The complete picture of investment performance.",
    category: "returns",
  },

  // ── Signals ─────────────────────────────────────────────────────────
  {
    id: "buy-signal",
    term: "BUY Signal (Score 65-100)",
    short: "Model considers the stock attractively valued with solid fundamentals.",
    long: "Our model considers this stock attractively valued with solid fundamentals and positive momentum. Not a guarantee — always research before investing.",
    category: "signals",
  },
  {
    id: "watch-signal",
    term: "WATCH Signal (Score 40-64)",
    short: "Reasonable fundamentals but not compelling enough to add at current price.",
    long: "Interesting stock with reasonable fundamentals but not compelling enough to add more at current price. Monitor for improvement.",
    category: "signals",
  },
  {
    id: "hold-signal",
    term: "HOLD Signal (Score below 40)",
    short: "Currently unattractive — possibly overvalued, weak fundamentals or negative momentum.",
    long: "Currently unattractive based on our scoring model. May be overvalued, have weak fundamentals, or negative price momentum.",
    category: "signals",
  },
  {
    id: "five-layer-score",
    term: "Six-Layer Composite Score",
    short: "Composite 0-100 across 6 layers: valuation, growth, balance sheet, intrinsic value, AI, sector context.",
    long: "Our composite score (0-100) combines six layers. Layer 1 — Current Value vs sector peers (25 pts) · Layer 2 — Historical Growth Quality (25 pts) · Layer 3 — Balance Sheet Health (15 pts) · Layer 4 — Graham + DDM Fair Value (20 pts) · Layer 5 — AI Report + News Sentiment (15 pts) · Layer 6 — Sector Context (10 pts, refreshed weekly). The raw 0-110 sum is normalised back to 0-100, then the Technical Bonus (±10 from TradingView momentum) is added. BUY ≥ 65, WATCH 40-64, HOLD < 40.",
    category: "signals",
    aliases: ["composite score", "score", "5-layer", "6-layer", "six-layer"],
  },
  {
    id: "layer-6-sector-context",
    term: "Layer 6 — Sector Context",
    short: "Claude scores each CSE sector 0-10 once a week on cycle, valuation, macro, and foreign sentiment.",
    long: "Layer 6 is a sector-level overlay scored once per week (Sundays 21:00 SLT). Claude evaluates each CSE sector across four sub-axes: Cycle Position (0-3), Valuation vs History (0-3), Macro Tailwinds (0-2), and Foreign-Investor Sentiment (0-2). The same Layer 6 score applies to every stock in that sector — so a great company in a structurally weak sector still gets a small drag from Layer 6, while companies in strong cyclical sectors pick up a tailwind.",
    category: "signals",
    aliases: ["sector context", "layer 6", "sector score", "L6"],
  },
  {
    id: "sector-cycle-position",
    term: "Sector Cycle Position",
    short: "Where the sector sits in the economic cycle: early recovery, mid growth, late cycle, or contraction.",
    long: "A simplified four-stage classification of the sector's economic cycle. Early Recovery (3 pts) — emerging from a downturn, demand picking up. Mid Growth (2 pts) — earnings expanding, healthy momentum. Late Cycle (1 pt) — growth slowing, margins peaked. Contraction (0 pts) — earnings declining, defensive posture. Updated weekly by Claude based on the latest sector index moves and macro context.",
    category: "signals",
    aliases: ["cycle", "early recovery", "late cycle"],
  },
  {
    id: "sector-verdict",
    term: "OVERWEIGHT / NEUTRAL / UNDERWEIGHT (Sector Verdict)",
    short: "Layer 6 verdict label that summarises the sector's overall attractiveness right now.",
    long: "OVERWEIGHT (score ≥ 7) — the sector is well positioned across cycle, valuation, macro, and foreign sentiment, so stocks here pick up a tailwind in their composite score. NEUTRAL (4-6) — mixed signals, no special bias. UNDERWEIGHT (≤ 3) — structural or cyclical headwinds that drag every stock in the sector. Used in the Screener Sector column, the Sectors page, and the Market Intelligence Sector Scores grid.",
    category: "signals",
    aliases: ["overweight", "underweight", "neutral", "sector tilt"],
  },
  {
    id: "technical-signal",
    term: "Technical Signal",
    short: "TradingView momentum indicator from MAs, RSI, MACD. Independent of fundamentals.",
    long: "Price momentum indicator from TradingView based on moving averages, RSI, MACD and other technical indicators. STRONG BUY means most indicators point upward. Independent of company fundamentals.",
    category: "signals",
    aliases: ["tv signal", "tradingview"],
  },
  {
    id: "rsi",
    term: "RSI (Relative Strength Index)",
    short: "Momentum 0-100. >70 overbought, <30 oversold, 30-70 neutral.",
    long: "Momentum indicator from 0-100. Above 70 means potentially overbought (price may pull back). Below 30 means potentially oversold (price may bounce). Between 30-70 is neutral territory.",
    category: "signals",
  },

  // ── Portfolio ───────────────────────────────────────────────────────
  {
    id: "portfolio-health-score",
    term: "Portfolio Health Score",
    short: "Weighted avg of holdings' composite scores. 70+ Good · 50-69 Fair · <50 Needs attention.",
    long: "Weighted average of your holdings' composite scores. 70+ is Good, 50-69 is Fair, below 50 needs attention. Lower health means your holdings have weaker fundamentals overall.",
    category: "portfolio",
  },
  {
    id: "portfolio-yoc",
    term: "Yield on Cost (portfolio level)",
    short: "Total annual dividend income as % of total invested across all holdings.",
    long: "Your total annual dividend income as a percentage of your total amount invested across all holdings.",
    category: "portfolio",
  },
  {
    id: "exit-signal",
    term: "Exit Signal",
    short: "A flag suggesting you review or consider selling. App never sells automatically.",
    long: "A flag suggesting you review or consider selling a holding. Strong exit means the original investment case may have changed. Always your decision — the app never sells automatically.",
    category: "portfolio",
  },
  {
    id: "rebalancing",
    term: "Rebalancing",
    short: "Adjusting back toward target sector allocation. Best done by directing new funds, not selling.",
    long: "Adjusting portfolio back toward target sector allocation when it has drifted. Best done by directing new monthly investments toward underweight sectors rather than selling existing holdings.",
    category: "portfolio",
  },
  {
    id: "non-voting-shares",
    term: "Non-Voting Shares",
    short: "On CSE: voting (.N0000) vs non-voting (.X0000). Same dividend, lower price = higher yield.",
    long: "Some CSE companies have two share classes: voting (.N0000) and non-voting (.X0000). Non-voting shares have no voting rights at AGMs but pay identical dividends. They usually trade at a lower price, giving a higher dividend yield — making them attractive for income investors.",
    category: "portfolio",
    aliases: ["x shares", "voting", "n shares"],
  },
  {
    id: "average-cost-basis",
    term: "Average Cost Basis",
    short: "Total invested ÷ total shares when you've bought the same stock multiple times.",
    long: "When you buy the same stock multiple times at different prices, the average cost is the total amount invested divided by total shares. Used to calculate your true profit or loss.",
    category: "portfolio",
    aliases: ["avg cost"],
  },

  // ── CSE-Specific ────────────────────────────────────────────────────
  {
    id: "brokerage-commission",
    term: "Brokerage Commission",
    short: "Fee charged by your CSE broker for trades. Default 1.21% on both buy and sell.",
    long: "Fee charged by your CSE broker for executing trades. The CSE standard rate is 1.21% of transaction value for both buying and selling. A Rs.100,000 purchase costs Rs.1,210 in commission. The app includes brokerage in every cost, gain/loss, breakeven and projection figure so the numbers reflect what you actually pay.",
    category: "cse",
    aliases: ["commission", "trading fee", "broker fee"],
  },
  {
    id: "breakeven-price",
    term: "Breakeven Price",
    short: "The minimum sell price needed to recover your full cost incl. both legs of brokerage.",
    long: "The minimum price you'd need to sell at to recover your full investment including buying brokerage already paid AND the selling brokerage you'd pay on exit. Always slightly above your purchase price — typically 2.4-2.5% higher when buy & sell rates are both 1.21%.",
    category: "returns",
    aliases: ["breakeven", "break-even"],
  },
  {
    id: "capital-gains-tax-free",
    term: "Capital Gains — Tax Free (Sri Lanka)",
    short: "Profits from selling CSE listed shares are exempt from capital gains tax in Sri Lanka.",
    long: "Profits from selling shares listed on the Colombo Stock Exchange are exempt from capital gains tax in Sri Lanka — you keep 100% of your profit when you sell. There is no holding-period requirement and no tax filing needed for the gain itself. (Dividend payments are taxed separately via 10% withholding at source.)",
    category: "cse",
    aliases: ["cgt", "capital gains", "cgt exemption", "tax free"],
  },
  {
    id: "dividend-wht",
    term: "Dividend Withholding Tax (WHT)",
    short: "10% tax deducted from dividend payments at source by the company.",
    long: "A 10% tax deducted from dividend payments at source before you receive them. If a company declares Rs.10.50 dividend per share, you receive Rs.9.45 — the company remits Rs.1.05 to the tax authority on your behalf. You do not need to file separately for this tax. The app shows dividend income net of WHT throughout (yields, projections, monthly suggestions).",
    category: "cse",
    aliases: ["wht", "withholding tax", "10% tax"],
  },
  {
    id: "net-dividend",
    term: "Net Dividend",
    short: "The dividend you actually receive after 10% withholding tax. Net = Gross × 90%.",
    long: "The dividend amount you actually receive after the 10% Sri Lanka withholding tax is deducted at source. Net dividend = Gross DPS × 90%. All yield figures, dividend projections and monthly investment estimates in this app are shown net of WHT so they reflect what hits your bank account.",
    category: "cse",
    aliases: ["net dps", "after-tax dividend"],
  },
  {
    id: "gics-sector",
    term: "GICS Sector",
    short: "Global Industry Classification Standard — official categorisation used by CSE.",
    long: "Global Industry Classification Standard — the official system used to categorise companies by industry. CSE uses GICS to classify all listed companies.",
    category: "cse",
  },
  {
    id: "ex-dividend-date",
    term: "Ex-Dividend Date",
    short: "Cutoff to qualify for a dividend. Buy BEFORE this date or you miss the payment.",
    long: "The cutoff date to qualify for a dividend payment. You must own shares BEFORE this date to receive the dividend. Buying on or after the ex-dividend date means you miss that payment.",
    category: "cse",
    aliases: ["xd date"],
  },
  {
    id: "voting-vs-nonvoting",
    term: "Voting vs Non-Voting Shares",
    short: ".N0000 votes at AGMs; .X0000 has no voting rights but pays the same dividend.",
    long: "On CSE some companies have both types. Voting shares (.N0000) give you rights to vote at company meetings. Non-voting shares (.X0000) have no voting rights but receive the same dividend payment.",
    category: "cse",
  },

  // ── Market Intelligence & Macro ─────────────────────────────────────
  {
    id: "aspi",
    term: "ASPI (All Share Price Index)",
    short: "The main index of the Colombo Stock Exchange tracking all listed stocks.",
    long: "The All Share Price Index is the main benchmark of the Colombo Stock Exchange and tracks the value of all listed stocks. A rising ASPI means the overall market is going up; a falling ASPI means the market is going down.",
    category: "market-intelligence",
    aliases: ["all share index", "all share price index"],
  },
  {
    id: "sp-sl-20",
    term: "S&P SL 20",
    short: "Index of the 20 largest and most liquid stocks on CSE.",
    long: "The S&P Sri Lanka 20 is a market-cap weighted index of the 20 largest and most liquid stocks on CSE. Similar to the FTSE 100 in London. It tracks blue-chip performance more cleanly than the broader ASPI.",
    category: "market-intelligence",
    aliases: ["sp sl 20", "sp20", "s&p sl 20"],
  },
  {
    id: "awpr",
    term: "AWPR (Average Weighted Prime Rate)",
    short: "Average interest rate Sri Lankan banks charge their best customers.",
    long: "AWPR is the average interest rate Sri Lankan commercial banks charge their best customers. Falling AWPR is positive for stocks because borrowing costs reduce — companies pay less interest and consumers can spend more.",
    category: "market-intelligence",
    aliases: ["prime rate", "prime lending rate"],
  },
  {
    id: "t-bill-yield",
    term: "T-bill Yield",
    short: "Return on Sri Lanka government Treasury Bills — the risk-free benchmark.",
    long: "Treasury Bills are short-term government debt. The 364-day yield is the standard 'risk-free rate' for Sri Lanka. Stocks should return more than the T-bill yield to justify the extra risk. When T-bill yields fall, stocks become relatively more attractive.",
    category: "market-intelligence",
    aliases: ["treasury bill", "t bill", "risk free rate"],
  },
  {
    id: "foreign-net-flow",
    term: "Foreign Net Flow",
    short: "Net difference between foreign buys and sells on CSE each day.",
    long: "The net difference between what foreign investors buy and sell on CSE each day or period. Net buying is bullish (signal of foreign confidence). Net selling is bearish (foreign capital leaving). Sustained foreign outflows are usually a market headwind.",
    category: "market-intelligence",
    aliases: ["foreign flow", "foreign net flow", "net foreign", "fii flow"],
  },
  {
    id: "pmi",
    term: "PMI (Purchasing Managers Index)",
    short: "Survey measuring economic activity. Above 50 = expansion, below 50 = contraction.",
    long: "PMI is a survey of purchasing managers about new orders, production, employment and inventory. Above 50 indicates economic expansion; below 50 indicates contraction. Sri Lanka publishes separate Manufacturing and Services PMI numbers.",
    category: "market-intelligence",
    aliases: ["purchasing managers index", "manufacturing pmi", "services pmi"],
  },
  {
    id: "fx-reserves",
    term: "FX Reserves",
    short: "Sri Lanka's foreign-currency reserves held by the Central Bank.",
    long: "Foreign currency reserves held by the Central Bank of Sri Lanka, used to pay for imports and service foreign debt. Higher reserves indicate a stronger external position. After the 2022 crisis FX reserves were near zero; today they are several billion USD and considered stable.",
    category: "market-intelligence",
    aliases: ["foreign reserves", "central bank reserves"],
  },

  // ── Tax & Charges ───────────────────────────────────────────────────
  {
    id: "wht",
    term: "Dividend Withholding Tax (WHT)",
    short: "10% tax deducted from dividend payments at source before you receive them.",
    long: "Sri Lanka deducts a 10% withholding tax (WHT) from dividend payments at source before they reach your account. So a Rs.10 declared dividend pays you Rs.9 net. No separate filing is required — the company deducts and remits the tax directly.",
    category: "tax",
    aliases: ["withholding tax", "dividend tax"],
  },
  {
    id: "net-dividend",
    term: "Net Dividend",
    short: "The dividend you actually receive after 10% withholding tax.",
    long: "Net Dividend = Gross DPS × 90%. This is the actual cash you receive in your account after the 10% WHT is deducted. For example, a Rs.1.75 gross dividend pays you Rs.1.58 net.",
    category: "tax",
  },
  {
    id: "cgt-free",
    term: "Capital Gains — Tax Free",
    short: "Profits from selling CSE shares are exempt from capital gains tax in Sri Lanka.",
    long: "Profits from selling CSE-listed shares are completely exempt from capital gains tax in Sri Lanka. You keep 100% of any capital appreciation. Only dividend income is taxed (10% WHT, deducted at source).",
    category: "tax",
    aliases: ["capital gains tax", "cgt"],
  },
];

export function findGlossaryTerm(idOrTerm) {
  if (!idOrTerm) return null;
  const k = idOrTerm.toLowerCase().trim();
  return (
    GLOSSARY.find((t) => t.id === k) ||
    GLOSSARY.find((t) => t.term.toLowerCase() === k) ||
    GLOSSARY.find((t) => (t.aliases || []).map((a) => a.toLowerCase()).includes(k)) ||
    null
  );
}

export function searchGlossary(query) {
  if (!query) return GLOSSARY;
  const q = query.toLowerCase().trim();
  return GLOSSARY.filter((t) => {
    const haystack = [
      t.term,
      t.short,
      t.long,
      ...(t.aliases || []),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}
